export interface UserBase {
  name?: string
  email?: string
  profileImage?: string
}

export interface Parent extends UserBase {
  billing?: BillingInfo
}

export type Plan = 'trial' | 'monthly'
export type PlanTier = 'premium' | 'familia'
export type BillingStatus = 'active' | 'trial' | 'expired' | 'suspended'

export interface BillingInfo {
  plan: Plan
  planTier?: PlanTier
  status: BillingStatus
  trialStart?: string
  trialEnd?: string
  activeUntil?: string
  pricePerMonth?: number
  currency?: string
}

export type AgeBand = 'under13' | 'teen' | 'adult'
export type WebCategory = 'adult' | 'gambling' | 'drugs' | 'social' | 'violence'

export interface WebFilter {
  enabled: boolean
  ageBand: AgeBand
  categories: Record<WebCategory, boolean>
  customDomains: string[]
}

export interface AppInfo {
  appName?: string
  packageName?: string
  blocked?: boolean
}

export interface Contact {
  contactName?: string
  contactNumber?: string
}

export interface LocationInfo {
  latitude?: number
  longitude?: number
  fenceDiameter?: number
  fenceCenterLatitude?: number
  fenceCenterLongitude?: number
  outOfFence?: boolean
  geoFence?: boolean
}

export interface Message {
  senderPhoneNumber?: string
  messageBody?: string
  timeReceived?: string
  contactName?: string
}

export interface Call {
  callType?: string
  phoneNumber?: string
  contactName?: string
  callTime?: string
  callDurationInSeconds?: string
}

export interface ScreenLock {
  hours?: number
  minutes?: number
  locked?: boolean
  lockUntil?: string | null
  note?: string | null
}

export interface UnlockRequest {
  requestedAt?: string
  status?: string
}

export interface DailyQuota {
  enabled: boolean
  dailyMinutes: number // 0 = sin límite
}

export type WeekDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
export interface DayRoutine {
  enabled: boolean
  from: string // "21:00"
  to: string // "07:00"
}
export type Routines = Record<WeekDay, DayRoutine>
export const WEEK_DAYS: { id: WeekDay; label: string }[] = [
  { id: 'monday', label: 'Lunes' },
  { id: 'tuesday', label: 'Martes' },
  { id: 'wednesday', label: 'Miércoles' },
  { id: 'thursday', label: 'Jueves' },
  { id: 'friday', label: 'Viernes' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
]

export interface UninstallRequest {
  code: string // 6 dígitos
  expiresAt: string // ISO
  by: string // parent uid
  createdAt: string
}

export type UpdateTarget = 'all' | 'windows' | 'android'
export interface UpdateConfig {
  windowsVersion: string // ej "1.8.6"
  androidVersion: string // ej "1.8.6"
  target: UpdateTarget // a qué agente va
  notes: string // changelog mostrado en popup
  updatedAt: string // ISO
  updatedBy: string // admin uid
  force: boolean // si true, agente actualiza inmediato aunque ya esté en versión
}

export interface Child extends UserBase {
  parentEmail?: string
  apps?: AppInfo[]
  Contacts?: Contact[]
  location?: LocationInfo
  messages?: Record<string, Message>
  calls?: Record<string, Call>
  screenLock?: ScreenLock // solo bloqueo manual (locked)
  dailyQuota?: DailyQuota // cuota diaria separada de rutina
  routines?: Routines
  uninstallRequest?: UninstallRequest
  unlockRequest?: UnlockRequest
  appDeleted?: boolean
  lastSeen?: string
  deviceType?: string
  webFilter?: WebFilter
  agentVersion?: string
  lastCrash?: string
  browsingHistory?: { url: string; title: string; time: string }[]
  youtubeHistory?: { title: string; channel: string; time: string; url?: string }[]
  windows?: {
    activeApp?: string
    activeTitle?: string
    usage?: Record<string, number>
  }
}

export type PaymentMethod = 'wompi' | 'mercadopago' | 'transfer'

export interface PaymentRequest {
  uid: string
  name: string
  email: string
  requestedAt: string
  status: string
  plan?: string
  method?: PaymentMethod
  receiptImage?: string
  amountPaid?: number
}

export interface PaymentGatewayConfig {
  wompiUrl?: string
  mercadoPagoUrl?: string
  // Links con monto exacto POR PLAN (recomendado: un link por plan en cada
  // pasarela). Si están vacíos se usan wompiUrl/mercadoPagoUrl como fallback.
  wompiPremiumUrl?: string
  wompiFamiliaUrl?: string
  mercadoPagoPremiumUrl?: string
  mercadoPagoFamiliaUrl?: string
  whatsappNumber?: string
  adminEmail?: string
  bankName?: string
  accountType?: string
  accountNumber?: string
  beneficiaryName?: string
  documentId?: string
}
